type RuntimeScriptConstructor = new (id: string) => {
    render: () => Promise<void> | void;
};

type RuntimeScriptWindow = Window & {
    __truSites?: {
        [key: string]: unknown;
        initRuntimeScriptElement?: (element: HTMLElement, commonScriptId?: string) => void;
    };
};

function installRuntimeScriptsBootstrap(): void {
    class RuntimeScriptsBootstrap {

        private static readonly INITIALIZATION_DELAY = 30;
        private static readonly RUNTIME_SCRIPT_OBJECT_ATTRIBUTE = "data-runtime-script-object-name";
        private static readonly RUNTIME_SCRIPT_COMMON_ID_ATTRIBUTE = "data-runtime-script-common-id";
        private static readonly initializationTimers = new Map<string, number>();

        public static initAll(): void {
            document
                .querySelectorAll<HTMLElement>(`[${RuntimeScriptsBootstrap.RUNTIME_SCRIPT_OBJECT_ATTRIBUTE}]`)
                .forEach(element => RuntimeScriptsBootstrap.initElement(element));
        }

        public static initElement(element: HTMLElement, commonScriptId?: string): void {
            const status = element.getAttribute("data-local-script-status");
            if (!element.id || status === "complete" || status === "rendering") {
                return;
            }

            if (document.readyState === "loading") {
                document.addEventListener(
                    "DOMContentLoaded",
                    () => RuntimeScriptsBootstrap.initElement(element, commonScriptId),
                    { once: true }
                );
                return;
            }

            RuntimeScriptsBootstrap.scheduleInitialization(element, commonScriptId);
        }

        private static scheduleInitialization(element: HTMLElement, commonScriptId?: string): void {
            const elementId = element.id;
            const pendingTimer = RuntimeScriptsBootstrap.initializationTimers.get(elementId);
            if (pendingTimer !== undefined) {
                window.clearTimeout(pendingTimer);
            }

            const timer = window.setTimeout(() => {
                if (RuntimeScriptsBootstrap.initializationTimers.get(elementId) !== timer) {
                    return;
                }

                RuntimeScriptsBootstrap.initializationTimers.delete(elementId);
                RuntimeScriptsBootstrap.tryInitialize(element, commonScriptId);
            }, RuntimeScriptsBootstrap.INITIALIZATION_DELAY);

            RuntimeScriptsBootstrap.initializationTimers.set(elementId, timer);
        }

        private static tryInitialize(element: HTMLElement, commonScriptId?: string): void {
            const status = element.getAttribute("data-local-script-status");
            if (!element.id || status === "complete" || status === "rendering") {
                return;
            }

            const RuntimeScriptObject = RuntimeScriptsBootstrap.getRuntimeScriptObject(element);
            if (RuntimeScriptObject) {
                void new RuntimeScriptObject(element.id).render();
                return;
            }

            const resolvedCommonScriptId = commonScriptId
                ?? element.getAttribute(RuntimeScriptsBootstrap.RUNTIME_SCRIPT_COMMON_ID_ATTRIBUTE)
                ?? undefined;
            const commonScript = resolvedCommonScriptId ? document.getElementById(resolvedCommonScriptId) : null;
            if (!commonScript) {
                element.setAttribute("data-local-script-status", "failed");
                return;
            }

            commonScript.addEventListener("load", () => RuntimeScriptsBootstrap.tryInitialize(element), { once: true });
            commonScript.addEventListener("error", () => element.setAttribute("data-local-script-status", "failed"), { once: true });
        }

        private static getRuntimeScriptObject(element: HTMLElement): RuntimeScriptConstructor | undefined {
            const scriptObjectName = element.getAttribute(RuntimeScriptsBootstrap.RUNTIME_SCRIPT_OBJECT_ATTRIBUTE);
            const runtimeWindow = window as RuntimeScriptWindow;
            return scriptObjectName
                ? runtimeWindow.__truSites?.[scriptObjectName] as RuntimeScriptConstructor | undefined
                : undefined;
        }

    }

    const runtimeWindow = window as RuntimeScriptWindow;
    runtimeWindow.__truSites = runtimeWindow.__truSites || {};
    runtimeWindow.__truSites.initRuntimeScriptElement = RuntimeScriptsBootstrap.initElement;

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => RuntimeScriptsBootstrap.initAll(), { once: true });
    } else {
        RuntimeScriptsBootstrap.initAll();
    }
}

installRuntimeScriptsBootstrap();
